import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteApps, selectSiteInfoStatus } from '@store/slices/settingsSlice.tsx';
import '@styles/LoadingScreen.css';

export interface RequireAppProps {
    children: ReactNode;
    /**
     * App key, or keys, required to access the route (e.g., 'cms', 'eshop', 'erp')
     */
    requiredApp: string | readonly string[];
    /**
     * Path to redirect to if the app is not enabled.
     * @default '/'
     */
    fallbackPath?: string;
}

/**
 * Route guard that restricts access to routes requiring a specific app to be enabled.
 * Uses Redux state (`state.settings.apps`) to check if the app is enabled.
 *
 * If `infoStatus` is still loading, shows a spinner.
 * If the required app is not in the enabled apps list, redirects to `fallbackPath`.
 * Otherwise, renders the children.
 */
export const RequireApp = ({
    children,
    requiredApp,
    fallbackPath = '/',
}: RequireAppProps) => {
    const location = useLocation();
    const apps = useAppSelector(selectSiteApps) ?? [];
    const infoStatus = useAppSelector(selectSiteInfoStatus);

    // Show loading while site info is being fetched
    if (infoStatus === 'loading') {
        return (
            <div className="loading-screen">
                <Spin size="large" tip="Cargando..." />
            </div>
        );
    }

    const requiredApps = Array.isArray(requiredApp) ? requiredApp : [requiredApp];
    const missingApps = requiredApps.filter((app) => !apps.includes(app));
    const isAppEnabled = missingApps.length === 0;

    if (!isAppEnabled) {
        return (
            <Navigate
                to={fallbackPath}
                state={{ from: location, reason: 'app-not-enabled', requiredApp, missingApps }}
                replace
            />
        );
    }

    return <>{children}</>;
};

export default RequireApp;
